/**
 * How report rows read in the console: receipt states, gateway states,
 * latency and failure breakdowns. Pure functions, no Angular.
 */

/** Receipt states that mean the SMS will not arrive (same set as DeliveryService). */
export const FAILED_RECEIPT_STATES = ['REJECTED', 'UNDELIVERABLE', 'EXPIRED', 'DELETED', 'SKIPPED'] as const;

export type FailedReceiptState = (typeof FAILED_RECEIPT_STATES)[number];

/** "UNDELIVERABLE" → "Undeliverable", "EN_ROUTE" → "En route". */
export function humanState(raw: string | null | undefined): string {
  const words = String(raw ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/**
 * Label and app-status keyword for a delivery receipt state. app-status picks
 * the dot from the keyword, so failures are passed as "failed" rather than as
 * themselves ("UNDELIVERABLE" contains "deliver").
 */
export function receiptStatus(raw: string | null | undefined): { label: string; tone: string } {
  const state = String(raw ?? '').trim().toUpperCase();
  if (!state) return { label: 'No receipt', tone: 'inactive' };
  // "Undeliverable" reads as plain "Failed" everywhere in the console.
  const label = state === 'UNDELIVERABLE' ? 'Failed' : humanState(state);
  if (state === 'DELIVERED') return { label, tone: 'delivered' };
  if ((FAILED_RECEIPT_STATES as readonly string[]).includes(state)) return { label, tone: 'failed' };
  if (state === 'EN_ROUTE') return { label, tone: 'sending' };
  if (state === 'UNKNOWN') return { label, tone: 'inactive' };
  return { label, tone: 'pending' };
}

/** Plain-language reason for each failed receipt state, for tooltips and CSV. */
export function failureReason(raw: string | null | undefined): string {
  switch (String(raw ?? '').trim().toUpperCase()) {
    case 'REJECTED':
      return 'The network or gateway refused the message.';
    case 'UNDELIVERABLE':
      return 'The network could not deliver it, often because the number is invalid, inactive or barred.';
    case 'EXPIRED':
      return 'The phone stayed unreachable until the message expired.';
    case 'DELETED':
      return 'The message was removed before it was delivered.';
    case 'SKIPPED':
      return 'Not sent to this number, for example because it is blacklisted or invalid.';
    default:
      return '';
  }
}

// ── SMS gateway log ──

export type GatewayOutcome = 'delivered' | 'failed' | 'pending' | 'unknown';

/**
 * Groups the gateway's status words (SMPP short forms such as DELIVRD and
 * UNDELIV, or full words) into four outcomes. Failure words are checked
 * first, because "UNDELIVERED" contains "DELIVERED".
 */
export function gatewayOutcome(raw: string | null | undefined): GatewayOutcome {
  const state = String(raw ?? '').trim().toUpperCase();
  if (!state) return 'unknown';
  if (/UNDELIV|REJECT|EXPIR|FAIL|DELETE|SKIP|ERROR|^ERR|INVALID|BLACKLIST|BARRED|BLOCK/.test(state)) return 'failed';
  if (/DELIVRD|DELIVERED|^DELIV/.test(state)) return 'delivered';
  if (/ENROUTE|EN_ROUTE|ACCEPT|SUBMIT|QUEUE|PENDING|SENT|SCHEDUL|BUFFER|PROCESS|DEFERR/.test(state)) return 'pending';
  return 'unknown';
}

/**
 * app-status keyword for a gateway outcome. Messages on their way show as
 * "sending" (blue), the same as an en-route receipt in the other reports.
 */
export function gatewayTone(outcome: GatewayOutcome): string {
  switch (outcome) {
    case 'delivered':
      return 'delivered';
    case 'failed':
      return 'failed';
    case 'pending':
      return 'sending';
    default:
      return 'inactive';
  }
}

/** Plain label for a gateway outcome, as the status filter and CSV name it. */
export function gatewayOutcomeLabel(outcome: GatewayOutcome): string {
  switch (outcome) {
    case 'delivered':
      return 'Delivered';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'On the way';
    default:
      return 'No status';
  }
}

/** SMPP short forms and the full words the gateway reports, in plain language. */
const GATEWAY_STATE_LABELS: Record<string, string> = {
  DELIVRD: 'Delivered',
  DELIVERED: 'Delivered',
  UNDELIV: 'Failed',
  UNDELIVERABLE: 'Failed',
  UNDELIVERED: 'Failed',
  REJECTD: 'Rejected',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  DELETED: 'Deleted',
  ACCEPTD: 'Accepted',
  ACCEPTED: 'Accepted',
  ENROUTE: 'En route',
  EN_ROUTE: 'En route',
  SUBMITTED: 'Submitted',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
  UNKNOWN: 'Unknown',
};

/**
 * Status label for a gateway row: "DELIVRD" reads "Delivered", "UNDELIV"
 * reads "Failed". Words the console does not know fall back to the
 * outcome label, so the table agrees with its status filter.
 */
export function gatewayStatusLabel(raw: string | null | undefined): string {
  const state = String(raw ?? '').trim().toUpperCase();
  return GATEWAY_STATE_LABELS[state] ?? gatewayOutcomeLabel(gatewayOutcome(state));
}

/** Time from submission to the delivery receipt, or null when either is missing. */
export function latencyMs(
  submitted: string | null | undefined,
  delivered: string | null | undefined
): number | null {
  if (!submitted || !delivered) return null;
  const from = new Date(submitted).getTime();
  const to = new Date(delivered).getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null;
  return to - from;
}

/** "under 1 s", "4 s", "2 min 5 s", "3 h 10 min". */
export function formatLatency(ms: number | null): string {
  if (ms === null) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 1) return 'under 1 s';
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const rest = seconds % 60;
    return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export interface BreakdownRow {
  name: string;
  total: number;
  failed: number;
  /** failed / total, 0–1. */
  share: number;
}

/**
 * Failures grouped by a field (network, country), worst first: the groups
 * with the most failures, then the highest failure share. Groups without a
 * name are collected under `unnamed`.
 */
export function failureBreakdown<T>(
  rows: readonly T[],
  nameOf: (row: T) => string | null | undefined,
  isFailed: (row: T) => boolean,
  unnamed = 'Not reported'
): BreakdownRow[] {
  const groups = new Map<string, { total: number; failed: number }>();
  for (const row of rows) {
    const name = String(nameOf(row) ?? '').trim() || unnamed;
    const group = groups.get(name) ?? { total: 0, failed: 0 };
    group.total += 1;
    if (isFailed(row)) group.failed += 1;
    groups.set(name, group);
  }
  return [...groups.entries()]
    .map(([name, { total, failed }]) => ({ name, total, failed, share: total ? failed / total : 0 }))
    .sort((a, b) => b.failed - a.failed || b.share - a.share || b.total - a.total || a.name.localeCompare(b.name));
}

/** "1 message", "12 messages". */
export function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}
